/**
 * The sky's palette run for a mission: which `BARON.PAL` entries it steps, how
 * they are distributed over the dome, and the ground colour that goes with
 * them. `time-of-day.ts` is the other half of `sun.c` — where the sun and moon
 * hang; this file is what colour the sky is while they do.
 *
 * ## The rule this file exists to hold
 *
 * **Every sky pixel is a `BARON.PAL` entry.** The original paints its sky with
 * `setSkyShades(lo, hi, heights)`, which walks *consecutive palette indices*
 * and gives each one a run of scanlines — there is no blending anywhere in it.
 * So a run here names its entries (`indices` beside `colours`, the
 * `world/terrain-palette.ts` `TerrainRamp` shape) and a test re-derives every
 * one of them from `data/baked/palettes/BARON.PAL.json`. A run that carried
 * only its two endpoints could not state that rule, because the 48-63 ramp is
 * not a straight line in RGB and no mix of its ends passes through its middle.
 *
 * ## AUTHENTIC — the daytime banded sky, endpoints, table and orientation
 *
 * `shading-and-paint.md` §2 places `setSkyShades(lo, hi, bandHeightTable)` at
 * `RB.EXE` seg `3ED9:0020` (the routine behind the `"bad sky shades"` assert).
 * It "paints the sky as a **descending run of consecutive palette indices from
 * `hi` down to `lo`**" — the original's sky is a *banded vertical gradient over
 * a palette ramp*, not a single colour.
 *
 * Three things about that run are **settled by measurement**, and each names
 * where its evidence lives:
 *
 * - the endpoints are `48` and `63`, constants in the only two `setSkyShades`
 *   call sites in the binary (`overcast-sky.ts`, `enhanced-sky.md` §2.6);
 * - the distribution is `ds:0x796`, sixteen `int16` scanline counts — see
 *   `SKY_BAND_SCANLINES`;
 * - **48 is the zenith**, measured off in-flight captures of `RB.EXE` rather
 *   than argued from what haze looks like, with every band matching its table
 *   entry scanline for scanline. The evidence table is on
 *   `SKY_BAND_SCANLINES`.
 *
 * What is still a port choice is how many *degrees* a scanline is worth, since
 * the original's field of view has never been recovered — that is the whole of
 * `SKY_RUN_EXTENT_DEG`, and it is one clearly-labelled number rather than an
 * exponent nobody can check.
 *
 * A clear-sky daytime sortie over Verdun measured the visible sky as **flat
 * index 48**, "no gradient, no haze band" (`world-terrain.md` §7.1, VERIFIED).
 * That measurement is what pins `SKY_RUN_EXTENT_DEG`, and the tension between
 * it and the table's 69-of-81 rows is recorded there rather than resolved.
 *
 * Dawn, dusk and night have no measurement and no table behind them; their runs
 * come from `BARON.PAL`'s own *unshaded* ramps (`SHADES.DAT` is zero across all
 * of them, i.e. the palette reserves them for flats and skies rather than model
 * paint): 1-7 twilight blue, 243-254 sunset orange. They keep the exponent,
 * because there is nothing better to give them.
 *
 * ## What is recovered here, and what is not
 *
 * Recovered: the daytime endpoints (48 and 63, constants at the binary's only
 * two `setSkyShades` call sites), the band distribution (`SKY_BAND_SCANLINES`,
 * `ds:0x796`), and which end is the zenith — all three measured, with the
 * evidence on `SKY_BAND_SCANLINES`.
 *
 * Not recovered: how many degrees a scanline is worth, which is
 * `SKY_RUN_EXTENT_DEG` and is the one free parameter; and anything at all about
 * dawn, dusk or night, which are declared port extensions built from the
 * palette's own unshaded ramps.
 */

import type { TimeOfDay } from "@red-opus/engine";
import { GROUND_RELIEF_RAMP, type PaletteColour } from "../world/terrain-palette.ts";
import {
  DAWN_SKY_COLOURS,
  DAWN_SKY_RAMP,
  DAYTIME_SKY_COLOURS,
  DAYTIME_SKY_RAMP,
  DUSK_SKY_COLOURS,
  DUSK_SKY_RAMP,
  NIGHT_SKY_COLOURS,
  NIGHT_SKY_RAMP,
  SKY_BAND_SCANLINES,
  SKY_RUN_EXTENT_DEG,
} from "./sky-ramps.ts";

export { DAYTIME_SKY_RAMP, SKY_BAND_SCANLINES, SKY_RUN_EXTENT_DEG } from "./sky-ramps.ts";

export type { PaletteColour };

/**
 * The sky as `setSkyShades` paints it: a run of **consecutive `BARON.PAL`
 * entries**, stepped in hard bands from the zenith overhead down to the skyline.
 * A one-entry run is the degenerate flat sky, which is what the Verdun captures
 * measured and what this port draws with the toggle off.
 *
 * ## Why a run names its entries rather than its two endpoints (G18)
 *
 * A run of `{ zenith, horizon, bands }` painted with `mix(zenith, horizon,
 * step)` quantises *where* a band falls while still **interpolating its
 * colour**, and a mix of two palette entries does not land on a third. The
 * 48-63 ramp is not a straight line in RGB — 48 `#7db6c3` and 49 `#7db6be`
 * share a red, then red climbs in fours while blue holds flat for five entries
 * at a time — so an interpolated daytime sky draws `#81b6c2` and `#84b6c1`,
 * neither of which is in `BARON.PAL` at all. Measured on the swapchain, that
 * arrangement puts **100 % of sky pixels off-palette**.
 *
 * The original blends nothing: `setSkyShades(lo, hi, heights)` walks
 * *consecutive palette indices* and gives each one a run of scanlines. Naming
 * the entries is what makes the same true here, in the same shape —
 * `indices` beside `colours`, checked against the real bytes by a test — that
 * `world/terrain-palette.ts`'s `TerrainRamp` uses for the ground.
 */
export interface SkyRun {
  /** The `BARON.PAL` indices this run steps, zenith first and skyline last. */
  readonly indices: readonly number[];
  /**
   * Those indices' colours, in the same order.
   * `test/sky-dome.test.ts` re-derives them from
   * `data/baked/palettes/BARON.PAL.json` and fails if one is not the real byte,
   * so this list cannot drift from the file.
   */
  readonly colours: readonly PaletteColour[];
  /**
   * How hard the run is pushed down towards the skyline: the band a point of
   * the dome falls in is `(1 - sin(elevation)) ** concentration`. Per run
   * rather than global because the four times of day are not equally
   * constrained — see `TREATED_LOOKS`.
   *
   * Used only when `heights` is absent, i.e. at the three hours for which no
   * band table was ever recovered.
   */
  readonly concentration: number;
  /**
   * **The recovered distribution (G9)**: how many of the original's scanlines
   * each entry of this run gets, in the same order as `indices`.
   *
   * Present only for daytime, because `ds:0x796` is the only band table in the
   * game and `setSkyShades(48, 63, ds:0x796)` is the only call that uses it on
   * a run of more than one entry. When it is present the run is laid out by
   * *scanline* against `extentDeg` and the exponent is not consulted at all;
   * when it is absent the exponent is all there is. See `SKY_BAND_SCANLINES`.
   */
  readonly heights?: readonly number[];
  /**
   * How far above the horizon the whole run reaches, in degrees — the one
   * number in the recovered path that is **not** recovered. See
   * `SKY_RUN_EXTENT_DEG`.
   */
  readonly extentDeg?: number;
}

/** Scanlines in a run laid out by `heights`, i.e. the height of the whole ramp on the original's screen. */
export function skyRunScanlines(run: SkyRun): number {
  return (run.heights ?? []).reduce((total, height) => total + height, 0);
}

/** How many hard steps a run is drawn in — one per palette entry it names. */
export function skyBandCount(run: SkyRun): number {
  return run.colours.length;
}

/** The entry overhead. */
export function skyZenith(run: SkyRun): PaletteColour {
  return run.colours[0] ?? MEASURED_SKY_BLUE;
}

/** The entry at the skyline — what `sky-dressing.ts` tints the authentic stars against. */
export function skyHorizon(run: SkyRun): PaletteColour {
  return run.colours[run.colours.length - 1] ?? MEASURED_SKY_BLUE;
}

/** A flat, one-entry run: no bands, no shader, one `MeshBasicMaterial`. */
export function flatSkyRun(index: number, colour: PaletteColour): SkyRun {
  return { indices: [index], colours: [colour], concentration: 1 };
}

/**
 * Daytime's concentration. The half-way band lands at `asin(1 - 0.5**(1/n))` of
 * elevation, which at 10 is **3.4 degrees** — a haze band hugging the skyline,
 * leaving the rest of a level view at the measured `#7db6c3`.
 */
export const DAYTIME_HAZE_CONCENTRATION = 10;

/**
 * Dawn, dusk and night. The same arithmetic puts the half-way band at **12
 * degrees**, a wash across the lower sky rather than a line along it — the
 * gradient the user asked for, spent at the three hours no measurement
 * constrains.
 */
export const TWILIGHT_CONCENTRATION = 3;

/** `BARON.PAL` 48, the sky colour measured in the original (`world-terrain.md` §7.1). */
export const MEASURED_SKY_BLUE: PaletteColour = 0x7db6c3;
/** `BARON.PAL` 143, the ground colour measured in the original (`world-terrain.md` §7.1). */
export const MEASURED_GROUND_GREEN: PaletteColour = 0x4d5928;

/** The measured picture: one flat sky colour, no bands. What the toggle turns *off* to. */
export const MEASURED_SKY_RUN: SkyRun = flatSkyRun(48, MEASURED_SKY_BLUE);

/** Sky and ground together, since a dimmed sky over a noon-bright ground reads worse than either alone. */
export interface SkyLook {
  readonly sky: SkyRun;
  readonly ground: PaletteColour;
}

export const MEASURED_LOOK: SkyLook = { sky: MEASURED_SKY_RUN, ground: MEASURED_GROUND_GREEN };

/**
 * PORT EXTENSION (module doc): a run and a ground colour per time of day, every
 * sky endpoint and every ground colour being a `BARON.PAL` entry out of a ramp
 * the palette reserves for flats.
 *
 * - **daytime** walks the recovered sky ramp itself, 48 (`#7db6c3`) to 63
 *   (`#b6b6b6`). Both ends are the original's; only the decision to walk it is
 *   ours. Its concentration is much the tightest of the four, and that is a
 *   correction rather than a taste: at the broad setting the other three use, a
 *   measured capture of `/fly` in level flight came back **`#b6b6b6` as the
 *   single most common sky colour on screen** — because a level view only ever
 *   sees the bottom 25 degrees of the dome, so "most of the dome stays at the
 *   zenith colour" and "most of the *screen* stays at it" are different claims
 *   and only the second one is what `world-terrain.md` §7.1 measured. At
 *   `DAYTIME_HAZE_CONCENTRATION` the ramp is confined to the few degrees just
 *   above the skyline, where haze actually is, and level flight reads as the
 *   flat `#7db6c3` it was measured as.
 * - **dawn** and **dusk** put the twilight-blue ramp (1-7) overhead and the
 *   sunset-orange ramp (243-254) at the skyline, dusk sitting deeper into both
 *   than dawn. They carry the broad gradient, which is the one the user asked
 *   for, and they can: nothing was ever measured at either hour, so there is no
 *   flat picture to stay faithful to.
 * - **night** runs black (0) down the head of the same twilight blue — dark
 *   without being unflyable; see `sky-dressing.ts` on why visibility here is
 *   presentation only, and `NIGHT_SKY_RAMP` for why its skyline entry is the
 *   one endpoint G18 moved.
 *
 * Ground colours come from 88-99, `BARON.PAL`'s dark-green ramp, in the same
 * descending order.
 */
const TREATED_LOOKS: Readonly<Record<TimeOfDay, SkyLook>> = {
  daytime: {
    sky: {
      indices: DAYTIME_SKY_RAMP,
      colours: DAYTIME_SKY_COLOURS,
      concentration: DAYTIME_HAZE_CONCENTRATION,
      heights: SKY_BAND_SCANLINES,
      extentDeg: SKY_RUN_EXTENT_DEG,
    },
    ground: MEASURED_GROUND_GREEN,
  },
  dawn: {
    sky: { indices: DAWN_SKY_RAMP, colours: DAWN_SKY_COLOURS, concentration: TWILIGHT_CONCENTRATION },
    ground: 0x2c4520,
  },
  dusk: {
    sky: { indices: DUSK_SKY_RAMP, colours: DUSK_SKY_COLOURS, concentration: TWILIGHT_CONCENTRATION },
    ground: 0x283c1c,
  },
  night: {
    sky: { indices: NIGHT_SKY_RAMP, colours: NIGHT_SKY_COLOURS, concentration: TWILIGHT_CONCENTRATION },
    ground: 0x243414,
  },
};

/**
 * The sky and ground for a mission. `treated` is false only under the
 * `measured` debug reference (`effects-settings.ts`), where every mission gets
 * the measured daytime picture whatever hour it briefs — the honest fallback:
 * the captures behind `MEASURED_LOOK` are one daytime clear-sky sortie, and
 * nothing was ever measured for the other three hours. **Both treatments a
 * player can choose pass `true`**, because the hour is mission data rather than
 * a setting.
 */
export function skyLookFor(timeOfDay: TimeOfDay | undefined, treated: boolean): SkyLook {
  if (!treated || timeOfDay === undefined) return MEASURED_LOOK;
  return TREATED_LOOKS[timeOfDay];
}

/**
 * How far down its own ramp the *terrain* walks at this hour — the number
 * `three/relief-ground.ts` shifts every face by.
 *
 * **Derived, not chosen.** The four ground colours above were picked by the
 * sky lane for the flat plane, and it turns out all four are entries of
 * `GROUND_RELIEF_RAMP`: daytime is its centre (143), dawn is 99, dusk 98 and
 * night 96. So the hour's dimming *is* a walk down the same run the slope
 * shading walks, and this function only has to say how many steps — which
 * means a future retune of `TREATED_LOOKS` moves the terrain with it
 * automatically instead of leaving the two half a ramp apart.
 *
 * Returns 0 for daytime, for a mission that briefs no hour, and under the
 * `measured` reference — in all three cases the ground keeps the measured
 * `#4d5928` exactly, which is the whole contract of that reference.
 */
export function groundRampShiftFor(timeOfDay: TimeOfDay | undefined, treated: boolean): number {
  return groundRampShiftForColour(skyLookFor(timeOfDay, treated).ground);
}

/**
 * The same walk, taken from a ground colour directly.
 *
 * This is the form the scene actually uses, because with ENHANCED SKY on the
 * ground the backdrop plane is painted is not `skyLookFor`'s: the mission's sky
 * word darkens it further (`enhanced-sky-look.ts`'s `groundFloor`). Deriving
 * both the plane and the relief mesh from **one colour** is what stops the
 * flat ground and the hills in front of it walking two different distances down
 * this ramp — `sky-dressing.ts`'s `dressingGroundColour` is the one place that
 * colour is decided, and `scene-boot.ts` feeds it straight into here.
 *
 * A colour that is not on the ramp returns 0 rather than throwing, which is the
 * honest failure: the terrain keeps the measured `#4d5928` and the mismatch is
 * visible rather than silent. Every colour either layer can produce is on the
 * ramp today, and `enhanced-sky-look.test.ts` pins that.
 */
export function groundRampShiftForColour(colour: PaletteColour): number {
  const at = GROUND_RELIEF_RAMP.colours.indexOf(colour);
  return at < 0 ? 0 : at - GROUND_RELIEF_RAMP.centre;
}
